import { Color, Spacing } from '@persona-id/design-tokens';
import { isEmpty } from 'lodash';
import { Fragment, useMemo, VFC } from 'react';
import styled from 'styled-components';

import { LoadingShimmerParagraph } from 'dashboard/components/LoadingShimmer/LoadingShimmer';
import {
  BinaryOperator,
  ConditionalRule,
  ConditionalRuleValueConfig,
  generateJsonSchemaConfig,
  getConditionalRuleParts,
  getOperatorDisplayName,
  JsonLogicValue,
  LogicBlockOrV2,
  parseFieldPath,
  RuleValueTypes,
  UsableObjectConfig,
} from 'dashboard/lib/workflows';
import { getRuleSideDisplay, isEmptyRule, LogicBlockV2 } from 'dashboard/lib/workflows/conditional';
import fieldMetas from 'dashboard/lib/workflows/fieldMetas';
import { TransactionType } from 'dashboard/models/transaction-type';
import { Trigger } from 'dashboard/models/trigger';
import { Paragraph } from 'design-system/components/Text';
import { EventType } from 'lib/constants/events';
import { ApiModel } from 'lib/interfaces/api';
import { eventTypeFor, hasNoPossibilities } from 'lib/workflows';

const RulesContainer = styled.div`
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const GridRow = styled.div<{ isLoadingSchema?: boolean }>`
  display: grid;
  grid-template-columns: ${({ isLoadingSchema }) => (isLoadingSchema ? '100%' : '15% 85%')};
`;

const RuleRow = styled.div`
  display: flex;
  white-space: wrap;
`;

const InitialCondition = styled(Paragraph)`
  color: ${({ theme }) => theme.color.text.primary};
`;

const NoneDisplay = styled(Paragraph)`
  color: ${({ theme }) => theme.color.text.tertiary};
`;

const RightValue = styled.span`
  margin-left: ${Spacing[2]};
  overflow: hidden;
`;

const Operator = styled.span`
  margin-left: ${Spacing[2]};
  color: ${Color.TextSecondary};
`;

interface WorkflowCardTriggerCriteriaProps {
  isLoadingSchema: boolean;
  trigger: Trigger;
  usableObjects: UsableObjectConfig[];
  transactionTypes: TransactionType[];
}

const WorkflowCardTriggerCriteria: VFC<WorkflowCardTriggerCriteriaProps> = ({
  isLoadingSchema,
  trigger,
  usableObjects,
  transactionTypes,
}) => {
  if (trigger.type === ApiModel.TriggerEvent) {
    const rules: LogicBlockOrV2 | boolean = trigger.attributes.rules;
    // event triggers have only 1 OR
    // Note: (jonathan.wang) Since rules is a union type, we narrow so we can type orRules strictly
    const orRules: LogicBlockV2[] = typeof rules === 'boolean' ? [] : rules[BinaryOperator.Or];

    const transactionTypeId =
      trigger.attributes['template-filters']?.['transaction-type']?.[0] || undefined;
    const transactionType =
      transactionTypeId && transactionTypes.length
        ? transactionTypes.find((tt) => tt.id === transactionTypeId)
        : null;
    const shouldRenderTransactionTypeRule =
      eventTypeFor(trigger) === EventType.Transaction && !!transactionType;

    // Conditionally render TxnType rule if it exists
    const renderTransactionTypeRuleIfApplicable = (index: number) => {
      return shouldRenderTransactionTypeRule ? (
        <GridRow isLoadingSchema={isLoadingSchema}>
          <InitialCondition>{index === 0 ? 'Where' : 'Or'}</InitialCondition>
          <RuleRow>
            <span>TxnType</span>
            <Operator>equals</Operator>
            <RightValue data-test='workflow-card-trigger-criteria__transaction-type'>
              {transactionType?.attributes?.name ?? null}
            </RightValue>
          </RuleRow>
        </GridRow>
      ) : null;
    };

    if (
      (trigger.attributes.rules === true || isEmpty(trigger.attributes.rules)) &&
      !shouldRenderTransactionTypeRule
    ) {
      return <NoneDisplay>None</NoneDisplay>;
    }

    return (
      <RulesContainer data-test='workflow-card-trigger-criteria__container'>
        {orRules.length === 0 && renderTransactionTypeRuleIfApplicable(0)}
        {orRules.map((andRules, i) => (
          <Fragment key={i}>
            {renderTransactionTypeRuleIfApplicable(i)}
            {andRules[BinaryOperator.And].map((andRule, j) => {
              // NOTE: we use a 1 column grid while the schema is loading so the shimmers display correctly
              return (
                <GridRow isLoadingSchema={isLoadingSchema} key={j}>
                  {!isLoadingSchema && (
                    <InitialCondition>
                      {shouldRenderTransactionTypeRule
                        ? ''
                        : j === 0
                          ? i === 0
                            ? 'Where'
                            : 'Or'
                          : ''}
                    </InitialCondition>
                  )}
                  <Rule
                    andRule={andRule}
                    usableObjects={usableObjects}
                    isLoadingSchema={isLoadingSchema}
                  />
                </GridRow>
              );
            })}
          </Fragment>
        ))}
      </RulesContainer>
    );
  } else {
    return <NoneDisplay>None</NoneDisplay>;
  }
};

export default WorkflowCardTriggerCriteria;

interface RuleProps {
  andRule: ConditionalRule;
  isLoadingSchema: boolean;
  usableObjects: UsableObjectConfig[];
}

const Rule: VFC<RuleProps> = ({ andRule, isLoadingSchema, usableObjects }) => {
  const { operator, valueLeftConfig, valueRightConfig } = getConditionalRuleParts(andRule);
  const { jsonSchema, tokensToDisplayValue } = useMemo(() => {
    return generateJsonSchemaConfig({
      usableObjects,
      useOriginal: true,
    });
  }, [usableObjects]);

  const renderOneSide = (
    ruleValueConfig: ConditionalRuleValueConfig,
    otherConfig?: ConditionalRuleValueConfig,
  ) => {
    const otherPath = otherConfig?.type === RuleValueTypes.Field ? otherConfig.paths[0] : '';
    const { criteriaVariableKey } = parseFieldPath(otherPath, usableObjects);

    const formatValues = (valueToFormat: JsonLogicValue) => {
      const stringifiedValue = Array.isArray(valueToFormat)
        ? valueToFormat
        : String(valueToFormat).replace(/,/g, ', ');
      return (
        fieldMetas[criteriaVariableKey]?.displayValueMapper(stringifiedValue) || stringifiedValue
      );
    };

    return formatValues(
      getRuleSideDisplay({
        jsonSchema,
        otherConfig,
        ruleValueConfig,
        tokensToDisplayValue,
        tokensToSteps: {},
        usableObjects,
      }) ?? '',
    );
  };

  if (isEmptyRule(valueLeftConfig, valueRightConfig)) {
    return <div>Empty Rule</div>;
  }

  return isLoadingSchema ? (
    <RuleRow>
      <LoadingShimmerParagraph
        data-test='workflow-card-trigger-criteria__loading-shimmer'
        width='60%'
      />
    </RuleRow>
  ) : (
    <RuleRow>
      <span>{renderOneSide(valueLeftConfig)}</span>
      <Operator>{operator != null ? getOperatorDisplayName(operator) : ''}</Operator>
      {!hasNoPossibilities(operator) && valueRightConfig && (
        <RightValue>{renderOneSide(valueRightConfig, valueLeftConfig)}</RightValue>
      )}
    </RuleRow>
  );
};
